import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
    Briefcase, MapPin, Clock, Building2, ArrowLeft, Loader2, DollarSign,
    Monitor, Sun, Moon, RotateCw, Zap, Upload, CheckCircle2, AlertCircle,
    Star, ChevronRight, FileText, X,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPE_LABELS = {
    full_time: 'Full Time', part_time: 'Part Time', contract: 'Contract',
};
const LOCATION_LABELS = {
    wfh: 'Remote', office: 'On-site', hybrid: 'Hybrid',
};
const SHIFT_LABELS = {
    day: 'Day Shift', night: 'Night Shift', flexible: 'Flexible Hours', rotating: 'Rotating Shifts',
};
const SHIFT_ICONS = {
    day: Sun, night: Moon, flexible: Zap, rotating: RotateCw,
};
const EXPERIENCE_LABELS = {
    entry: 'Entry Level', mid: 'Mid Level', senior: 'Senior', lead: 'Lead', executive: 'Executive',
};

function formatSalary(range) {
    if (!range || !range.visible) return null;
    const fmt = (n) => {
        if (n >= 1000) return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1)}k`;
        return n.toLocaleString();
    };
    const currency = range.currency || 'USD';
    if (range.min && range.max) return `${currency} ${fmt(range.min)} - ${fmt(range.max)}`;
    if (range.min) return `From ${currency} ${fmt(range.min)}`;
    if (range.max) return `Up to ${currency} ${fmt(range.max)}`;
    return null;
}

export default function JobDetailPage() {
    const { orgSlug, jobId } = useParams();
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Application form state
    const [name, setName] = useState('');
    const [email, setEmail] = useState('');
    const [phone, setPhone] = useState('');
    const [resume, setResume] = useState(null);
    const [screeningAnswers, setScreeningAnswers] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const fileInputRef = useRef(null);
    const formRef = useRef(null);

    useEffect(() => {
        axios.get(`${API}/public/careers/${orgSlug}/jobs/${jobId}`)
            .then(res => {
                setJob(res.data);
                // Initialize screening answers
                if (res.data.screening_questions?.length) {
                    setScreeningAnswers(
                        res.data.screening_questions.map(q => ({ question: q.question, answer: '' }))
                    );
                }
            })
            .catch(() => setError('Job not found'))
            .finally(() => setLoading(false));
    }, [orgSlug, jobId]);

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const validTypes = [
            'application/pdf',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        ];
        if (!validTypes.includes(file.type)) {
            setSubmitError('Please upload a PDF or DOCX file.');
            return;
        }
        if (file.size > 10 * 1024 * 1024) {
            setSubmitError('File size must be under 10MB.');
            return;
        }
        setSubmitError(null);
        setResume(file);
    };

    const removeFile = () => {
        setResume(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const updateAnswer = (index, answer) => {
        setScreeningAnswers(prev => {
            const next = [...prev];
            next[index] = { ...next[index], answer };
            return next;
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError(null);
        setSubmitting(true);

        try {
            const formData = new FormData();
            formData.append('name', name);
            formData.append('email', email);
            if (phone) formData.append('phone', phone);
            if (resume) formData.append('resume', resume);
            if (screeningAnswers.length) {
                formData.append('screening_answers', JSON.stringify(screeningAnswers));
            }

            await axios.post(
                `${API}/public/careers/${orgSlug}/jobs/${jobId}/apply`,
                formData,
                { headers: { 'Content-Type': 'multipart/form-data' } }
            );
            setSubmitted(true);
        } catch (err) {
            setSubmitError(
                err.response?.data?.detail || 'Something went wrong. Please try again.'
            );
        } finally {
            setSubmitting(false);
        }
    };

    // --- Render states ---

    if (loading) return (
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAFAF5' }}>
            <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
        </div>
    );

    if (error || !job) return (
        <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#FAFAF5' }}>
            <div className="text-center">
                <Briefcase className="w-12 h-12 text-stone-300 mx-auto mb-3" />
                <p className="text-stone-500 mb-4">{error || 'Job not found'}</p>
                <Link to={`/careers/${orgSlug}`} className="text-sm text-stone-600 hover:text-stone-900 underline">
                    Back to careers
                </Link>
            </div>
        </div>
    );

    const salaryStr = formatSalary(job.salary_range);
    const ShiftIcon = SHIFT_ICONS[job.shift] || Clock;
    const locationStr = job.location?.city
        ? `${job.location.city}${job.location.country ? `, ${job.location.country}` : ''}`
        : null;

    return (
        <div className="min-h-screen" style={{ backgroundColor: '#FAFAF5' }}>
            {/* Header */}
            <div className="bg-stone-900 text-white">
                <div className="max-w-4xl mx-auto px-6 py-12">
                    <Link
                        to={`/careers/${orgSlug}`}
                        className="inline-flex items-center gap-1.5 text-sm text-stone-400 hover:text-stone-200 transition-colors mb-6"
                    >
                        <ArrowLeft className="w-4 h-4" />
                        Back to {job.org_name} Careers
                    </Link>
                    <h1
                        className="text-3xl font-bold mb-3"
                        style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}
                    >
                        {job.title}
                    </h1>
                    <div className="flex flex-wrap items-center gap-4 text-sm text-stone-300">
                        {job.department && (
                            <span className="flex items-center gap-1.5">
                                <Building2 className="w-4 h-4" /> {job.department}
                            </span>
                        )}
                        {locationStr && (
                            <span className="flex items-center gap-1.5">
                                <MapPin className="w-4 h-4" /> {locationStr}
                            </span>
                        )}
                        <span className="flex items-center gap-1.5">
                            <Clock className="w-4 h-4" /> {TYPE_LABELS[job.employment_type] || job.employment_type}
                        </span>
                        <span className="inline-flex px-2.5 py-0.5 rounded-full bg-stone-800 text-xs text-stone-300">
                            {LOCATION_LABELS[job.work_location] || job.work_location}
                        </span>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="max-w-4xl mx-auto px-6 py-10">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
                    {/* Main column */}
                    <div className="lg:col-span-2 space-y-8">
                        {/* Job Details Badges */}
                        <div className="flex flex-wrap gap-3">
                            {job.experience_level && (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-stone-200 text-sm text-stone-700">
                                    <Star className="w-4 h-4 text-amber-500" />
                                    {EXPERIENCE_LABELS[job.experience_level] || job.experience_level}
                                </span>
                            )}
                            {job.shift && (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-stone-200 text-sm text-stone-700">
                                    <ShiftIcon className="w-4 h-4 text-stone-500" />
                                    {SHIFT_LABELS[job.shift] || job.shift}
                                </span>
                            )}
                            {salaryStr && (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-stone-200 text-sm text-stone-700">
                                    <DollarSign className="w-4 h-4 text-emerald-500" />
                                    {salaryStr}
                                </span>
                            )}
                            {job.work_location && (
                                <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white border border-stone-200 text-sm text-stone-700">
                                    <Monitor className="w-4 h-4 text-blue-500" />
                                    {LOCATION_LABELS[job.work_location] || job.work_location}
                                </span>
                            )}
                        </div>

                        {/* Description */}
                        {job.description && (
                            <div>
                                <h2
                                    className="text-lg font-semibold text-stone-900 mb-4"
                                    style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}
                                >
                                    About the Role
                                </h2>
                                <div
                                    className="prose prose-stone max-w-none text-stone-700 leading-relaxed"
                                    dangerouslySetInnerHTML={{
                                        __html: job.description.replace(/\n/g, '<br/>')
                                    }}
                                />
                            </div>
                        )}

                        {/* Required Skills */}
                        {job.required_skills?.length > 0 && (
                            <div>
                                <h2
                                    className="text-lg font-semibold text-stone-900 mb-4"
                                    style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}
                                >
                                    Required Skills
                                </h2>
                                <div className="flex flex-wrap gap-2">
                                    {job.required_skills.map((skill, i) => (
                                        <span
                                            key={i}
                                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm ${
                                                skill.importance === 'required'
                                                    ? 'bg-stone-900 text-white'
                                                    : 'bg-stone-100 text-stone-700 border border-stone-200'
                                            }`}
                                        >
                                            {skill.name}
                                            <span className={`text-xs ${
                                                skill.importance === 'required'
                                                    ? 'text-stone-400'
                                                    : 'text-stone-400'
                                            }`}>
                                                {skill.importance === 'required' ? 'Required' : 'Preferred'}
                                            </span>
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sidebar - quick info on desktop */}
                    <div className="hidden lg:block">
                        <div className="bg-white rounded-xl border border-stone-200 p-5 sticky top-6">
                            <h3
                                className="text-sm font-semibold text-stone-900 mb-4"
                                style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}
                            >
                                Quick Info
                            </h3>
                            <div className="space-y-3 text-sm">
                                {job.department && (
                                    <div className="flex items-center gap-2 text-stone-600">
                                        <Building2 className="w-4 h-4 text-stone-400 flex-shrink-0" />
                                        <span>{job.department}</span>
                                    </div>
                                )}
                                {locationStr && (
                                    <div className="flex items-center gap-2 text-stone-600">
                                        <MapPin className="w-4 h-4 text-stone-400 flex-shrink-0" />
                                        <span>{locationStr}</span>
                                    </div>
                                )}
                                <div className="flex items-center gap-2 text-stone-600">
                                    <Clock className="w-4 h-4 text-stone-400 flex-shrink-0" />
                                    <span>{TYPE_LABELS[job.employment_type] || job.employment_type}</span>
                                </div>
                                {job.experience_level && (
                                    <div className="flex items-center gap-2 text-stone-600">
                                        <Star className="w-4 h-4 text-stone-400 flex-shrink-0" />
                                        <span>{EXPERIENCE_LABELS[job.experience_level] || job.experience_level}</span>
                                    </div>
                                )}
                                {salaryStr && (
                                    <div className="flex items-center gap-2 text-stone-600">
                                        <DollarSign className="w-4 h-4 text-stone-400 flex-shrink-0" />
                                        <span>{salaryStr}</span>
                                    </div>
                                )}
                                {job.shift && (
                                    <div className="flex items-center gap-2 text-stone-600">
                                        <ShiftIcon className="w-4 h-4 text-stone-400 flex-shrink-0" />
                                        <span>{SHIFT_LABELS[job.shift] || job.shift}</span>
                                    </div>
                                )}
                            </div>
                            {job.published_at && (
                                <p className="text-xs text-stone-400 mt-4 pt-3 border-t border-stone-100">
                                    Posted {new Date(job.published_at).toLocaleDateString('en-US', {
                                        month: 'long', day: 'numeric', year: 'numeric'
                                    })}
                                </p>
                            )}
                            <button
                                onClick={() => formRef.current?.scrollIntoView({ behavior: 'smooth' })}
                                className="w-full mt-4 py-2.5 bg-stone-900 text-white text-sm font-medium rounded-lg hover:bg-stone-800 transition-colors"
                            >
                                Apply Now
                            </button>
                        </div>
                    </div>
                </div>

                {/* Application Form */}
                <div ref={formRef} className="mt-12 pt-10 border-t border-stone-200">
                    {submitted ? (
                        <div className="text-center py-16">
                            <CheckCircle2 className="w-14 h-14 text-emerald-500 mx-auto mb-4" />
                            <h2
                                className="text-2xl font-bold text-stone-900 mb-2"
                                style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}
                            >
                                Application Submitted
                            </h2>
                            <p className="text-stone-500 max-w-md mx-auto">
                                Thank you for applying to <strong>{job.title}</strong> at {job.org_name}.
                                We will review your application and get back to you soon.
                            </p>
                            <Link
                                to={`/careers/${orgSlug}`}
                                className="inline-flex items-center gap-1.5 mt-6 text-sm text-stone-600 hover:text-stone-900 transition-colors"
                            >
                                <ArrowLeft className="w-4 h-4" />
                                View more positions
                            </Link>
                        </div>
                    ) : (
                        <>
                            <h2
                                className="text-2xl font-bold text-stone-900 mb-2"
                                style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}
                            >
                                Apply for this Position
                            </h2>
                            <p className="text-stone-500 mb-8">
                                Fill out the form below to submit your application.
                            </p>

                            <form onSubmit={handleSubmit} className="max-w-2xl space-y-6">
                                {/* Name */}
                                <div>
                                    <label className="block text-sm font-medium text-stone-700 mb-1.5">
                                        Full Name <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="text"
                                        value={name}
                                        onChange={e => setName(e.target.value)}
                                        required
                                        placeholder="Enter your full name"
                                        className="w-full px-4 py-2.5 border border-stone-200 rounded-lg bg-white text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-900/10 focus:border-stone-300"
                                    />
                                </div>

                                {/* Email */}
                                <div>
                                    <label className="block text-sm font-medium text-stone-700 mb-1.5">
                                        Email Address <span className="text-red-500">*</span>
                                    </label>
                                    <input
                                        type="email"
                                        value={email}
                                        onChange={e => setEmail(e.target.value)}
                                        required
                                        placeholder="you@example.com"
                                        className="w-full px-4 py-2.5 border border-stone-200 rounded-lg bg-white text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-900/10 focus:border-stone-300"
                                    />
                                </div>

                                {/* Phone */}
                                <div>
                                    <label className="block text-sm font-medium text-stone-700 mb-1.5">
                                        Phone Number
                                    </label>
                                    <input
                                        type="tel"
                                        value={phone}
                                        onChange={e => setPhone(e.target.value)}
                                        placeholder="+1 (555) 000-0000"
                                        className="w-full px-4 py-2.5 border border-stone-200 rounded-lg bg-white text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-900/10 focus:border-stone-300"
                                    />
                                </div>

                                {/* Resume Upload */}
                                <div>
                                    <label className="block text-sm font-medium text-stone-700 mb-1.5">
                                        Resume
                                    </label>
                                    {resume ? (
                                        <div className="flex items-center gap-3 px-4 py-3 bg-white border border-stone-200 rounded-lg">
                                            <FileText className="w-5 h-5 text-stone-500 flex-shrink-0" />
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm text-stone-700 truncate">{resume.name}</p>
                                                <p className="text-xs text-stone-400">
                                                    {(resume.size / 1024 / 1024).toFixed(2)} MB
                                                </p>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={removeFile}
                                                className="p-1 text-stone-400 hover:text-stone-600 transition-colors"
                                            >
                                                <X className="w-4 h-4" />
                                            </button>
                                        </div>
                                    ) : (
                                        <button
                                            type="button"
                                            onClick={() => fileInputRef.current?.click()}
                                            className="w-full flex items-center justify-center gap-2 px-4 py-8 border-2 border-dashed border-stone-200 rounded-lg text-stone-500 hover:border-stone-300 hover:text-stone-600 hover:bg-white/50 transition-all cursor-pointer"
                                        >
                                            <Upload className="w-5 h-5" />
                                            <span className="text-sm">Upload PDF or DOCX (max 10MB)</span>
                                        </button>
                                    )}
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        accept=".pdf,.docx,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                                        onChange={handleFileChange}
                                        className="hidden"
                                    />
                                </div>

                                {/* Screening Questions */}
                                {job.screening_questions?.length > 0 && (
                                    <div className="space-y-6 pt-2">
                                        <h3
                                            className="text-base font-semibold text-stone-900"
                                            style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}
                                        >
                                            Screening Questions
                                        </h3>
                                        {job.screening_questions.map((q, i) => (
                                            <div key={i}>
                                                <label className="block text-sm font-medium text-stone-700 mb-1.5">
                                                    {q.question}
                                                    {q.required && <span className="text-red-500 ml-1">*</span>}
                                                </label>

                                                {q.type === 'text' && (
                                                    <textarea
                                                        value={screeningAnswers[i]?.answer || ''}
                                                        onChange={e => updateAnswer(i, e.target.value)}
                                                        required={q.required}
                                                        rows={3}
                                                        placeholder="Type your answer..."
                                                        className="w-full px-4 py-2.5 border border-stone-200 rounded-lg bg-white text-stone-900 placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-900/10 focus:border-stone-300 resize-none"
                                                    />
                                                )}

                                                {q.type === 'yes_no' && (
                                                    <div className="flex gap-4 mt-1">
                                                        {['Yes', 'No'].map(opt => (
                                                            <label key={opt} className="flex items-center gap-2 cursor-pointer">
                                                                <input
                                                                    type="radio"
                                                                    name={`screening_${i}`}
                                                                    value={opt}
                                                                    checked={screeningAnswers[i]?.answer === opt}
                                                                    onChange={() => updateAnswer(i, opt)}
                                                                    required={q.required}
                                                                    className="w-4 h-4 text-stone-900 border-stone-300 focus:ring-stone-900/20"
                                                                />
                                                                <span className="text-sm text-stone-700">{opt}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                )}

                                                {q.type === 'multiple_choice' && q.options?.length > 0 && (
                                                    <div className="space-y-2 mt-1">
                                                        {q.options.map((opt, j) => (
                                                            <label key={j} className="flex items-center gap-2 cursor-pointer">
                                                                <input
                                                                    type="radio"
                                                                    name={`screening_${i}`}
                                                                    value={opt}
                                                                    checked={screeningAnswers[i]?.answer === opt}
                                                                    onChange={() => updateAnswer(i, opt)}
                                                                    required={q.required}
                                                                    className="w-4 h-4 text-stone-900 border-stone-300 focus:ring-stone-900/20"
                                                                />
                                                                <span className="text-sm text-stone-700">{opt}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}

                                {/* Error */}
                                {submitError && (
                                    <div className="flex items-center gap-2 px-4 py-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
                                        <AlertCircle className="w-4 h-4 flex-shrink-0" />
                                        {submitError}
                                    </div>
                                )}

                                {/* Submit */}
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="inline-flex items-center justify-center gap-2 px-8 py-3 bg-stone-900 text-white font-medium rounded-lg hover:bg-stone-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 className="w-4 h-4 animate-spin" />
                                            Submitting...
                                        </>
                                    ) : (
                                        <>
                                            Submit Application
                                            <ChevronRight className="w-4 h-4" />
                                        </>
                                    )}
                                </button>
                            </form>
                        </>
                    )}
                </div>

                {/* Footer */}
                <div className="text-center mt-12 py-6 border-t border-stone-200">
                    <p className="text-xs text-stone-400">Powered by NurtureCRM</p>
                </div>
            </div>
        </div>
    );
}
